import type { FocusEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type {
  CollectionItem,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { ArrowMore, Check16 } from '@elfsight/icons';
import {
  Dropdown as DropdownComponent,
  DropdownItem
} from '@elfsight/widget-modal';
import { Icon, textColorMixin, textStyleMixin } from '@elfsight/widget-ui';
import type { FourValueTupleConvertable } from '@elfsight/widget-utils';
import {
  createReadableClass,
  getInvertedColor,
  isTransparentColor,
  roundTo4,
  to4VTuple
} from '@elfsight/widget-utils';
import { LINE_HEIGHT_RATIO } from '../constants';
import type { CommonFieldProps } from '../types';

export interface DropdownProps extends CommonFieldProps {
  options?: CollectionItem<{ name: string }>[];
  initialValue?: string;

  theme?: { type: string; accentColor: string };
  padding?: FourValueTupleConvertable<number>;
}

export function Dropdown({
  id,
  label,
  placeholder = 'Select Value',
  options,
  onChange,
  onFocus,
  value,
  initialValue,
  required,
  showLabel = true,
  theme,
  fieldBorderType,
  fieldBorderWidth,
  fieldBorderColor,
  fieldBorderRadius,
  fieldLabelPosition,
  fieldLabelFontStyle,
  fieldLabelColor,
  fieldColor: fontColor,
  fieldFillColor,
  fieldActiveColor,
  fieldFontStyle,
  padding
}: DropdownProps) {
  const firstRender = useRef(true);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // const selectRef = useRef<null | HTMLSelectElement>(null);
  const anchorElementRef = useRef<HTMLDivElement | null>(null);
  const [displayedValue, setDisplayedValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);

  const anchorWidth = anchorElementRef?.current?.getBoundingClientRect().width;

  const withUnderline = fieldBorderType === 'underline';
  const isTransparentFillColor = isTransparentColor(fieldFillColor);

  const fontSize = fieldFontStyle?.fontSize || 0;

  let fieldPadding = '10px 14px 10px 0';
  if (padding) {
    const [paddingTop, paddingRight, paddingBottom, paddingLeft] =
      to4VTuple(padding);

    fieldPadding = `${paddingTop}px ${paddingRight + 28}px
      ${paddingBottom}px ${paddingLeft}px`;
  } else if (!withUnderline || !isTransparentFillColor) {
    fieldPadding =
      fieldLabelPosition === 'inside'
        ? `${fontSize}px ${fontSize + 14}px ${fontSize}px ${fontSize}px`
        : `${Math.round((fontSize * 2) / 3)}px ${fontSize + 14}px ${Math.round(
            (fontSize * 2) / 3
          )}px ${fontSize}px`;
  }

  const borderColor = fieldBorderType ? fieldBorderColor : 'transparent';

  // TODO: get rid of this logic when AI generator bug is fixed
  const isSelected =
    initialValue && options?.some((option) => initialValue === option.id);

  useEffect(() => {
    const selected = isSelected ? initialValue : '';
    if (firstRender.current) {
      firstRender.current = false;

      if (isSelected && !value) {
        onChange?.(selected);
      }
      return;
    }

    if (selected) {
      onChange?.(selected);
    }
  }, [isSelected, initialValue]);

  useEffect(() => {
    const optionLabel = options?.find((option) => option.id === value);
    if (optionLabel) {
      setDisplayedValue(optionLabel.name);
    } else {
      setDisplayedValue(placeholder);
    }
  }, [value, options]);

  const clearFocusTimeout = () => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
  };

  const handleOpen = () => {
    if (!open || !focused) {
      setOpen(true);
      setFocused(true);
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    setFocused(false);
    setOpen(false);
  };

  const handleBlur: FocusEventHandler = ({
    target: { parentNode: targetElement },
    relatedTarget
  }) => {
    if (relatedTarget && !targetElement?.contains(relatedTarget)) {
      handleClose();
    }

    clearFocusTimeout();
  };

  const handleFocus = () => {
    onFocus?.();

    if (focusTimeoutRef.current !== null) {
      return;
    }

    focusTimeoutRef.current = setTimeout(() => {
      handleOpen();
      focusTimeoutRef.current = null;
    }, 150);
  };

  const handleChange = (id: string) => {
    onChange?.(id);
    handleClose();
  };

  useEffect(() => {
    if (initialValue) {
      onChange?.(initialValue);
    }
  }, [initialValue]);

  return (
    <>
      {!!label && fieldLabelPosition === 'inside' && showLabel && (
        <Label _textColor={fieldLabelColor} _textStyle={fieldLabelFontStyle}>
          {label}
        </Label>
      )}
      <Container ref={anchorElementRef}>
        <Select
          tabIndex={0}
          role="combobox"
          aria-expanded={open}
          aria-controls={id}
          aria-required={required}
          aria-label={label}
          onClick={handleOpen}
          onFocus={handleFocus}
          onBlur={handleBlur}
          defaultValue={initialValue}
          id={id}
          _themeType={theme?.type}
          _focused={focused}
          _padding={fieldPadding}
          _textColor={fontColor}
          _borderColor={borderColor}
          _activeColor={fieldActiveColor}
          _borderRadius={fieldBorderRadius}
          _withUnderline={withUnderline}
          _borderWidth={fieldBorderWidth}
          _backgroundColor={fieldFillColor}
          _textStyle={fieldFontStyle}
        >
          {displayedValue}
        </Select>
        <IconContainer _focused={focused}>
          <Icon fill={fontColor} size={24} component={ArrowMore} />
        </IconContainer>
      </Container>
      <DropdownComponent
        portalContainerElement={
          anchorElementRef.current ? anchorElementRef.current : undefined
        }
        onCloseRequest={handleClose}
        displayOver={false}
        width={anchorWidth ? anchorWidth + 'px' : '100%'}
        open={open}
        anchorElement={anchorElementRef.current}
        style={{ maxWidth: 'unset', cursor: 'default' }}
        fontColor={fontColor}
        background={getInvertedColor(String(fontColor))}
        fontSize={fieldFontStyle?.fontSize}
        fontWeight={fieldFontStyle?.fontWeight}
        aria-labelledby={id}
      >
        {!isSelected && placeholder && (
          <DropdownItem iconLeft={IconPlaceholder} disabled>
            {placeholder}
          </DropdownItem>
        )}
        {options?.map(({ id, name }) => {
          const isSelected = id === value;
          return (
            <DropdownItem
              iconLeft={isSelected ? Check16 : IconPlaceholder}
              selected={isSelected}
              onClick={() => handleChange(id)}
              key={id}
            >
              {name}
            </DropdownItem>
          );
        })}
      </DropdownComponent>
    </>
  );
}

const IconPlaceholder = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'dropdown-icon-placeholder'
  })
})`
  width: 16px;
  background: none;
`;

const Label = styled.label.attrs<{
  _textStyle?: TextStyleValue;
  _textColor?: string;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'dropdown-label'
  })
})`
  display: block;
  margin-bottom: 8px;
  line-height: ${({ _textStyle }) =>
    roundTo4((_textStyle?.fontSize || 0) * LINE_HEIGHT_RATIO)}px;
  ${textStyleMixin}
  ${textColorMixin}
`;

const Select = styled.div.attrs<{
  _themeType?: string;
  _textColor?: string;
  _withUnderline?: boolean;
  _borderWidth?: number;
  _padding?: string;
  _textStyle?: TextStyleValue;
  _borderRadius?: number;
  _backgroundColor?: string;
  _borderColor?: string;
  _activeColor?: string;
  _focused?: boolean;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'dropdown-select'
  })
})`
  flex: 1;
  z-index: 1;
  appearance: none;
  max-width: 100%;
  text-overflow: ellipsis;
  box-sizing: content-box !important;
  margin: 0 !important;
  height: auto !important;
  padding: ${({ _padding }) => _padding} !important;
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  line-height: ${({ _textStyle }) =>
    roundTo4((_textStyle?.fontSize || 0) * LINE_HEIGHT_RATIO)}px;
  min-height: ${({ _textStyle }) =>
    roundTo4((_textStyle?.fontSize || 0) * LINE_HEIGHT_RATIO)}px;
  background-color: ${({ _backgroundColor }) => _backgroundColor} !important;
  ${({ _withUnderline, _borderWidth }) =>
    _withUnderline
      ? css`
          border: none !important;
          border-bottom: ${_borderWidth}px solid !important;
        `
      : css`
          border: ${_borderWidth}px solid !important;
        `}
  border-color: ${({ _focused, _borderColor, _activeColor }) =>
    _focused ? _activeColor : _borderColor} !important;

  option {
    color: ${({ _themeType }) => (_themeType === 'dark' ? '#fff' : '#111')};
    background-color: ${({ _themeType }) =>
      _themeType === 'dark' ? '#111' : '#fff'};
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  ${textStyleMixin}
  ${textColorMixin}
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'dropdown-container'
  })
})`
  position: relative;
  display: flex;

  &,
  & * {
    cursor: pointer;
  }
`;

const IconContainer = styled.div.attrs<{ _focused: boolean }>({
  className: createReadableClass({
    component: 'fields',
    element: 'dropdown-icon-container'
  })
})`
  position: absolute;
  pointer-events: none;
  z-index: 2;
  right: 12px;
  top: 50%;
  transform: translate(0, -50%);
  ${({ _focused }) =>
    _focused &&
    css`
      transform: rotate(180deg) translate(0, 50%);
    `}
`;
